import {
  AliasType,
  Entity,
  Provider,
  SubmissionDetailInsuredEntityAliasData,
  SubmissionDetailInsuredEntityFundamentalsData,
  SubmissionDetailInsuredEntityIndustrySectorData,
  getFirstPaginatedNode,
  providerListWithoutPrimaryToDisplay,
} from '@converge/graphql';
import {
  ConvergeFormTable,
  CountryFormSelectField,
  FormContainerWithHeader,
  FormNumberField,
  FormNumberInputAdornmentField,
  FormTextField,
  IndustrySectorFormSelectField,
  RegionFormSelectField,
} from '../../../../../presentation';
import {
  capitalizeFirstLetter,
  convertInputToFormattedAmountUSD,
} from '@converge/utils';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { NAICS_PLACEHOLDER_CODE, getFieldProps } from '@converge/data';
import { useOutletContext } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  isReadonly?: boolean;
  isClaimsPolicyPage?: boolean;
}
interface IOutletProps {
  insuredEntity: Entity;
}
export const InsuredEntityPolicyHolderInfoFormSection = ({
  isReadonly,
  isClaimsPolicyPage,
}: IProps) => {
  const { t } = useTranslation();

  const { control, watch } = useFormContext();

  const countryCode = watch('insuredEntity.primaryAddress.iso2Code');

  const { insuredEntity } = useOutletContext<IOutletProps>();

  const { fields: tradingNamesField } = useFieldArray({
    control,
    name: 'insuredEntity.tradingNames',
  });

  const { fields: domainsField } = useFieldArray({
    control,
    name: 'insuredEntity.domains',
  });

  const { fields: otherAddressesField } = useFieldArray({
    control,
    name: 'insuredEntity.otherAddresses',
  });

  const { fields: otherIndustrySectorsField } = useFieldArray({
    control,
    name: 'insuredEntity.otherIndustrySectors',
  });
  const otherIndustrySectorsRows = (
    otherIndustrySectorsField as unknown as SubmissionDetailInsuredEntityIndustrySectorData[]
  ).filter((row) => row.naicsCode !== NAICS_PLACEHOLDER_CODE);

  // const { fields: sensitiveRecordTypeField } = useFieldArray({
  //   control,
  //   name: 'insuredEntity.sensitiveRecordTypes',
  // });

  const { fields: otherLegalAliases } = useFieldArray({
    control,
    name: 'insuredEntity.otherLegalAliases',
  });

  const { fields: otherFundamentals } = useFieldArray({
    control,
    name: 'insuredEntity.otherFundamentals',
  });

  const [RegionName, setRegionName] = useState<string>('State');
  const insuredAlias =
    insuredEntity?.aliases?.edges.find(
      (alias) =>
        alias?.node?.sources?.includes(Provider.Primary) &&
        alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.aliases?.edges.find(
      (alias) =>
        alias?.node?.sources?.includes(Provider.Converge) &&
        alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.aliases?.edges.find(
      (alias) => alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.aliases?.edges.find((alias) =>
      alias?.node?.sources?.includes(Provider.Primary)
    )?.node ??
    getFirstPaginatedNode(insuredEntity?.aliases);

  return (
    <FormContainerWithHeader
      title={t(
        'entities.insured.detail.content.sections.overview.title',
        'Insured'
      )}
    >
      {isClaimsPolicyPage ? (
        <FormTextField
          label={t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.legalName`,
            'Legal name'
          )}
          name="insuredEntity.primaryLegalAlias.name"
          disabled
        />
      ) : (
        <FormTextField
          label={t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.legalName`,
            'Legal name'
          )}
          name="insuredEntity.name"
          value={insuredAlias?.name}
          disabled
        />
      )}

      {isClaimsPolicyPage ? (
        <FormTextField
          label="Entity reference ID"
          name="insuredEntity.referenceId"
          value={insuredEntity?.referenceId}
          disabled
        />
      ) : (
        <FormTextField
          label="Entity reference ID"
          name="insuredEntityReferenceId"
          value={insuredEntity?.referenceId}
          disabled
        />
      )}
      <ConvergeFormTable
        title={'Identified Legal Names'}
        rows={
          otherLegalAliases as unknown as SubmissionDetailInsuredEntityAliasData[]
        }
        baseFieldPath="insuredEntity.otherLegalAliases"
        columns={[
          {
            id: 'name',
            label: 'Legal name',
          },
          {
            width: '20%',
            id: 'source',
            label: 'Data source',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources).join(', '),
          },
        ]}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryTradingName.name',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.dba`,
            'DBA'
          )
        )}
      />
      <ConvergeFormTable
        title={t(
          'underwriting.detail.content.sections.policyHolderInformation.fields.dbaList',
          'Identified DBA list'
        )}
        rows={
          tradingNamesField as unknown as SubmissionDetailInsuredEntityAliasData[]
        }
        baseFieldPath="insuredEntity.tradingNames"
        columns={[
          {
            id: 'name',
            label: 'DBA',
          },
          {
            width: '20%',
            id: 'source',
            label: 'Data source',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources).join(', '),
          },
        ]}
      />
      <ConvergeFormTable
        title={t('Identified Domains')}
        rows={domainsField}
        baseFieldPath="insuredEntity.domains"
        enablePagination
        columns={[
          {
            id: 'domain',
            label: 'Domain',
          },
          {
            width: '20%',
            id: 'source',
            label: 'Data source',
          },
        ]}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryAddress.street1',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.address1`,
            'Address 1'
          )
        )}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryAddress.street2',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.address2`,
            'Address 2'
          )
        )}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryAddress.city',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.city`,
            'City'
          )
        )}
      />
      <RegionFormSelectField
        {...getFieldProps(
          'insuredEntity.primaryAddress.region',
          isReadonly,
          capitalizeFirstLetter(RegionName)
        )}
        selectedCountry={countryCode}
        setRegionName={setRegionName}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryAddress.postalCode',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.postalCode`,
            'Postal Code'
          )
        )}
      />
      <CountryFormSelectField
        {...getFieldProps(
          'insuredEntity.primaryAddress.country',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.country`,
            'Country'
          )
        )}
      />
      <ConvergeFormTable
        title={'Identified Addresses'}
        rows={otherAddressesField}
        baseFieldPath={`insuredEntity.otherAddresses`}
        columns={[
          {
            id: 'street1',
            label: 'Address 1',
          },
          {
            id: 'street2',
            label: 'Address 2',
          },
          {
            id: 'city',
            label: 'City',
          },
          {
            id: 'regionName',
            label: 'Region',
          },
          {
            id: 'postalCode',
            label: 'Postal Code',
          },
          {
            id: 'countryName',
            label: 'Country',
          },
          {
            id: 'source',
            label: 'Data Source',
          },
        ]}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.companyType',
          true,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.companyType`,
            'Company Type'
          )
        )}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryFundamentals.yearEstablished',
          true,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.yearEstablished`,
            'Year Established'
          )
        )}
      />
      <IndustrySectorFormSelectField
        {...getFieldProps(
          'insuredEntity.primaryIndustrySector.naicsCode',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.industrySector`,
            'Industry sector'
          )
        )}
      />
      <ConvergeFormTable
        title={'Identified Industry Sectors'}
        rows={otherIndustrySectorsRows}
        baseFieldPath="insuredEntity.otherIndustrySectors"
        columns={[
          {
            id: 'name',
            label: 'Industry sector',
          },
          {
            id: 'naicsCode',
            label: 'NAICS',
          },
          {
            id: 'source',
            label: 'Data source',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources)?.join(', '),
          },
        ]}
      />

      <FormNumberInputAdornmentField
        adornmentText={'$'}
        {...getFieldProps(
          'insuredEntity.primaryFundamentals.revenue',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.revenue`,
            'Revenue'
          )
        )}
        thousandSeparator=","
      />
      <FormNumberField
        {...getFieldProps(
          'insuredEntity.primaryFundamentals.employees',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.employees`,
            'Employees'
          )
        )}
        thousandSeparator=","
      />
      <ConvergeFormTable
        title={t(
          'underwriting.detail.content.sections.policyHolderInformation.fields.identifiedFirmographics',
          'Identified firmographics'
        )}
        rows={
          otherFundamentals as unknown as SubmissionDetailInsuredEntityFundamentalsData[]
        }
        baseFieldPath="insuredEntity.otherFundamentals"
        columns={[
          {
            id: `revenue`,
            label: 'Revenue',
            renderValue: (value) => convertInputToFormattedAmountUSD(value),
          },
          {
            id: `employees`,
            label: 'Employees',
            renderValue: (value) => (value ? value.toLocaleString() : ''),
          },
          {
            id: 'source',
            label: 'Data source',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources)?.join(', '),
          },
        ]}
      />
      {/* <hr className="section-content-hr" /> */}

      {/* <ConvergeFormTable
        title={t(
          'underwriting.detail.content.sections.policyHolderInformation.fields.sensitiveRecordTypes',
          'Sensitive record types'
        )}
        rows={sensitiveRecordTypeField}
        baseFieldPath="insuredEntity.sensitiveRecordTypes"
        columns={[
          {
            id: `type`,
            label: 'Type',
          },
          {
            id: `quantity`,
            label: 'Quantity',
          },
        ]}
      />

      <hr className="section-content-hr" /> */}

      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryContact.firstName',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.contactFirstName`,
            'Contact first name'
          )
        )}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryContact.lastName',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.contactLastName`,
            'Contact last name'
          )
        )}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryContact.email',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.contactEmail`,
            'Contact email'
          )
        )}
      />
      <FormTextField
        {...getFieldProps(
          'insuredEntity.primaryContact.phone',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.contactPhone`,
            'Contact phone'
          )
        )}
      />
    </FormContainerWithHeader>
  );
};
